import iro from "@jaames/iro";
import { Color, Material } from "@cesium/engine";
import PolygonEntity from "../edit/PolygonEntity";
import DitapPopup from "./DitapPopup";
import ConstantUrl from "../icon/ConstantUrl";
import { DitapAlert, DitapMobileSelectPopup, Utils } from "../../..";

/**
 * PolygonEntity 스타일 변경 팝업 클래스.</br>
 * A pop-up class for changing the style of a PolygonEntity.
 * @extends DitapPopup
 *
 * @alias PopupStylePolygon
 * @constructor
 *
 * @param {PolygonEntity} polygonEntity 사용할 PolygonEntity 인스턴스.</br>The instance of PolygonEntity to change style.
 *
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.9777639655642,
 *   37.56676148138595,
 *    400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *  destination: destination,
 * });
 *
 * const position1 = new Ditap.Cartesian3.fromDegrees(
 *   126.9777639655642,
 *   37.56676148138595
 * );
 * const position2 = new Ditap.Cartesian3.fromDegrees(
 *   126.97776396708979,
 *   37.56658104096736
 * );
 * const position3 = new Ditap.Cartesian3.fromDegrees(
 *   126.97794279498576,
 *   37.566579036698215
 * );
 * const position4 = new Ditap.Cartesian3.fromDegrees(
 *   126.9779494810781,
 *   37.56676163390728
 * );
 *
 * // PolygonEntity instance 생성
 * const polygon = new Ditap.PolygonEntity(viewer, [
 *  position1,
 *  position2,
 *  position3,
 *  position4,
 * ]);
 *
 * const popup = new Ditap.PopupStylePolygon(polygon);
 */
class PopupStylePolygon extends DitapPopup {
  constructor(polygonEntity, ditapPaneManager) {
    // 부모
    const parent = polygonEntity?.viewer?.container;
    super(parent, ditapPaneManager);

    this.polygonEntity = undefined;

    // ENTITY
    if (polygonEntity instanceof PolygonEntity) {
      this.polygonEntity = polygonEntity;
    } else {
      console.error("Type doesn't match");
    }

    // 모바일일 경우 css 추가
    this.wrapper.classList.add("ditap-popup-mobile");
    this.wrapper.classList.add("ditap-style-change-popup");

    // 스타일 박스 생성
    const styleBox = this.createStyleBox();

    // 헤더 추가
    this.prependHeader(styleBox["header"]["title"]);

    // 컨텐츠 추가
    this.appendSubTitle(styleBox["content"]["subTitle"]);
    this.appendContent(styleBox["content"]["styleList"]);

    // 푸터 추가
    this.appendFooter(styleBox["footer"]["btnWrap"]);

    // 노드 세팅
    this.cancleBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-cancle-btn"
    )[0];
    this.okBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-ok-btn"
    )[0];
    this.typeSelectDiv = this.wrapper.getElementsByClassName(
      "js-ditap-style-select"
    )[0];
    this.typeSelectInput = this.wrapper.getElementsByClassName(
      "js-ditap-style-type"
    )[0];
    this.typeSelectButton = this.wrapper.getElementsByClassName(
      "js-ditap-style-type-button"
    )[0];
    this.typeOptionDiv = this.wrapper.getElementsByClassName(
      "js-ditap-style-type-opt"
    )[0];
    this.colorType = this.wrapper.getElementsByClassName(
      "js-ditap-style-type-opt-color-btn"
    )[0];
    this.imgType = this.wrapper.getElementsByClassName(
      "js-ditap-style-type-opt-img-btn"
    )[0];
    this.widthInput = this.wrapper.getElementsByClassName(
      "js-ditap-style-width"
    )[0];
    this.addWidthBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-add-width"
    )[0];
    this.subtractWidthBtn = this.wrapper.getElementsByClassName(
      "js-ditap-style-subtract-width"
    )[0];
    this.widthBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-width-box"
    )[0];
    this.vertexDiv = this.wrapper.getElementsByClassName(
      "js-ditap-vertex-div"
    )[0];
    this.vertexBtn = this.wrapper.getElementsByClassName(
      "js-ditap-vertex-item"
    )[0];
    this.vertexCheckBox = this.wrapper.getElementsByClassName(
      "js-ditap-vertex-check-box"
    )[0];
    this.lineColorBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-line-color-box"
    )[0];
    this.lineColorBtn = this.lineColorBox.getElementsByClassName(
      "js-ditap-style-color-btn"
    )[0];
    this.lineColorInput = this.lineColorBox.getElementsByClassName(
      "js-ditap-style-color"
    )[0];
    this.lineColorPickBox = this.lineColorBox.getElementsByClassName(
      "js-ditap-style-color-pickBox"
    )[0];
    this.lineColorPickerDiv = this.lineColorBox.getElementsByClassName(
      "js-ditap-style-color-picker"
    )[0];
    this.lineColorPickerOpenBtn = this.lineColorBox.getElementsByClassName(
      "js-ditap-color-open-btn"
    )[0];
    this.lineColorLists = this.lineColorBox
      .getElementsByClassName("js-ditap-style-color-list")[0]
      .getElementsByTagName("button");
    this.polygonColorBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-polygon-color-box"
    )[0];
    this.polygonColorBtn = this.polygonColorBox.getElementsByClassName(
      "js-ditap-style-color-btn"
    )[0];
    this.polygonColorInput = this.polygonColorBox.getElementsByClassName(
      "js-ditap-style-color"
    )[0];
    this.polygonColorPickBox = this.polygonColorBox.getElementsByClassName(
      "js-ditap-style-color-pickBox"
    )[0];
    this.polygonColorPickerDiv = this.polygonColorBox.getElementsByClassName(
      "js-ditap-style-color-picker"
    )[0];
    this.polygonColorPickerOpenBtn = this.polygonColorBox.getElementsByClassName(
      "js-ditap-color-open-btn"
    )[0];
    this.polygonColorLists = this.polygonColorBox
      .getElementsByClassName("js-ditap-style-color-list")[0]
      .getElementsByTagName("button");
    this.textureBox = this.wrapper.getElementsByClassName(
      "js-ditap-style-texture-box"
    )[0];
    this.textureImage = this.wrapper.getElementsByClassName(
      "js-ditap-style-texture-img"
    )[0];

    // contentDiv
    this.contentDiv = this.wrapper.getElementsByClassName(
      "ditap-popup-style-list"
    )[0];

    // 스타일 색상 선택 박스 생성
    // 선색상
    const lineColorPicker = this.createColorPicker(this.lineColorPickerDiv);
    this.lineColorPicker = lineColorPicker;

    // 면색상
    const polygonColorPicker = this.createColorPicker(
      this.polygonColorPickerDiv
    );
    this.polygonColorPicker = polygonColorPicker;

    // 팝업 오픈
    this.open(this.mainPopup);

    this.createAlert();

    this.createMobilePopup(parent);

    // 팝업 버튼 이벤트 추가
    this.createStyleEvent();

    // 기존 스타일 적용
    this.setPopupStylePolygon(polygonEntity);

    // 다이탭 로고
    this.base64Data = ConstantUrl.DITAP_LOGO_URL;

    // 텍스처 설정
    this.textureImage.style.backgroundImage = `URL(
      data:image/png;base64,${this.base64Data}
    )`;
    this.textureImage.style.backgroundSize = "contain";

    this.changeCloseBtn();
    // 드래그 기능 적용
    this.isMobile = Utils.isBodyWidthLessThan600();
    if (!this.isMobile) {
      this.enableDrag();
      this.enableTouch();
    }

    if (this.btnWrap) {
      const fullBtn = document.createElement("button");
      fullBtn.classList.add("ditap-popup-full-btn", "ditap-custom-button");

      this.btnWrap.prepend(fullBtn);
      this.fullBtn = this.btnWrap;

      this.fullBtn.addEventListener(
        "click",
        (this.toggleExpandPopup = () => {
          if (this.wrapper.classList.contains("expanded")) {
            this.wrapper.classList.remove("expanded");
          } else {
            this.wrapper.classList.add("expanded");
          }
        })
      );
    }

    this.createResizeEvent();
  }

  createMobilePopup(parent) {
    // 1. 스타일 팝업
    this.mobileTypeSelectPopup = new DitapMobileSelectPopup(parent);
    this.mobileTypeSelectPopup.addTitle("스타일");
    this.mobileTypeSelectPopup.closeCallback = () => {
      this.typeOptionDiv.classList.add("hide");
      this.typeSelectDiv.classList.remove("on");
    };

    // 2. 색상 팝업
    this.mobileLineColorPickerPopup = new DitapMobileSelectPopup(parent);
    this.mobileLineColorPickerPopup.addTitle("선색상");

    // 버튼 추가
    const btnWrap = document.createElement("div");
    const okBtn = document.createElement("button");
    const cancleBtn = document.createElement("button");
    okBtn.textContent = "확인";
    cancleBtn.textContent = "취소";
    okBtn.classList.add("ditap-popup-btn", "gold");
    btnWrap.classList.add("ditap-btn-wrap");
    okBtn.classList.add("ditap-popup-btn", "gold");
    cancleBtn.classList.add("ditap-popup-btn");
    btnWrap.appendChild(cancleBtn);
    btnWrap.appendChild(okBtn);
    this.mobileLineColorPickerPopup.appendFooter(btnWrap);

    okBtn.addEventListener("click", () => {
      const color = this.mobileLineColorInput.value;
      if (color.length === 7 && this.checkAvailableColor(color)) {
        this.mobileLineColorPickerPopup.close();
      } else {
        this.colorAlert.open();
        this.mobileLineColorInput.value = "#FFFFFF";
        this.lineColorPicker.color.red = 255;
        this.lineColorPicker.color.green = 255;
        this.lineColorPicker.color.blue = 255;
      }
    });

    cancleBtn.addEventListener("click", () => {
      this.mobileLineColorPickerPopup.close();
      this.changeColorPickerByInput(
        this.originInputValue,
        this.lineColorPicker,
        this.colorAlert,
        this.lineColorInput
      );
      this.lineColorPickBox.classList.add("hide");
    });

    const div = document.createElement("div");
    const inp = document.createElement("input");
    inp.classList.add("ditap-style-inp");
    inp.type = "text";
    inp.maxLength = "7";
    inp.value = this.polygonColorInput.value;
    div.append(inp);
    this.mobileLineColorPickerPopup.appendContent(div);

    this.mobileLineColorInput = inp;

    // 3. 윤곽 색상 박스 생성
    this.mobilePolygonColorPickerPopup = new DitapMobileSelectPopup(parent);
    this.mobilePolygonColorPickerPopup.addTitle("면색상");

    // 버튼 추가
    const btnWrap1 = document.createElement("div");
    const okBtn1 = document.createElement("button");
    const cancleBtn1 = document.createElement("button");
    okBtn1.textContent = "확인";
    cancleBtn1.textContent = "취소";
    okBtn1.classList.add("ditap-popup-btn", "gold");
    btnWrap1.classList.add("ditap-btn-wrap");
    okBtn1.classList.add("ditap-popup-btn", "gold");
    cancleBtn1.classList.add("ditap-popup-btn");
    btnWrap1.appendChild(cancleBtn1);
    btnWrap1.appendChild(okBtn1);
    this.mobilePolygonColorPickerPopup.appendFooter(btnWrap1);

    okBtn1.addEventListener("click", () => {
      const color = this.mobilePolygonColorInput.value;
      if (color.length === 7 && this.checkAvailableColor(color)) {
        this.mobilePolygonColorPickerPopup.close();
      } else {
        this.colorAlert.open();
        this.mobilePolygonColorInput.value = "#FFFFFF";
        this.polygonColorPicker.color.red = 255;
        this.polygonColorPicker.color.green = 255;
        this.polygonColorPicker.color.blue = 255;
      }
    });

    cancleBtn1.addEventListener("click", () => {
      this.mobilePolygonColorPickerPopup.close();
      this.changeColorPickerByInput(
        this.originPolygonInputValue,
        this.polygonColorPicker,
        this.polygonColorAlert,
        this.polygonColorInput
      );
      this.lineColorPickBox.classList.add("hide");
    });

    // 색상 박스 생성
    const div1 = document.createElement("div");
    const inp1 = document.createElement("input");
    inp1.classList.add("ditap-style-inp");
    inp1.type = "text";
    inp1.maxLength = "7";
    inp1.value = this.polygonColorInput.value;
    div1.append(inp1);
    this.mobilePolygonColorPickerPopup.appendContent(div1);

    this.mobilePolygonColorInput = inp1;
  }

  createAlert() {
    // 경고창 생성
    this.lineColorAlert = new DitapAlert({
      type: "warning",
      title: "경고",
      content1: "올바른 선 색상값을 입력해주세요. ",
      content2: "색상코드는 Hex코드로 형식으로 입력가능합니다.(ex: #FFFFFF)",
      buttons: "ONLYOK",
      open: false,
      callback: () => {},
    });

    this.polygonColorAlert = new DitapAlert({
      type: "warning",
      title: "경고",
      content1: "올바른 면 색상값을 입력해주세요. ",
      content2: "색상코드는 Hex코드로 형식으로 입력가능합니다.(ex: #FFFFFF)",
      buttons: "ONLYOK",
      open: false,
      callback: () => {},
    });

    this.widthAlert = new DitapAlert({
      type: "warning",
      title: "경고",
      content1: "올바른 굵기값을 입력해주세요.",
      content2: "지름은 1이상의 숫자로 입력가능합니다.",
      buttons: "ONLYOK",
      open: false,
      callback: () => {},
    });
  }

  createResizeEvent() {
    super.createResizeEvent();
    // 컬러픽커 리사이즈 이벤트
    window.addEventListener(
      "resize",
      (this.resizeEvent = () => {
        const currentIsMobile = Utils.isBodyWidthLessThan600();

        if (this.isMobile !== currentIsMobile) {
          this.changeCloseBtn();
          this.isMobile = currentIsMobile;
        }

        const width = Utils.getColorPickerWidth();

        this.lineColorPicker.resize(width);
        this.polygonColorPicker.resize(width);

        this.setMobilePopup();
      })
    );
  }

  setMobilePopup() {
    // 컬러픽커 모바일 팝업 이벤트
    const isMobile = Utils.isBodyWidthLessThan600();

    if (isMobile && this.lineColorBox.contains(this.lineColorPickBox)) {
      this.originInputValue = this.lineColorInput.value;
    }

    if (isMobile && this.polygonColorBox.contains(this.polygonColorPickBox)) {
      this.originPolygonInputValue = this.polygonColorInput.value;
    }

    this.moveMobileContentPopup(
      this.lineColorBox,
      this.mobileLineColorPickerPopup,
      this.lineColorPickBox
    );
    this.moveMobileContentPopup(
      this.polygonColorBox,
      this.mobilePolygonColorPickerPopup,
      this.polygonColorPickBox
    );

    this.moveMobileContentPopup(
      this.typeSelectDiv,
      this.mobileTypeSelectPopup,
      this.typeOptionDiv
    );
  }

  moveMobileContentPopup(container, popup, content) {
    const isMobile = Utils.isBodyWidthLessThan600();
    if (isMobile && container.contains(content)) {
      container.removeChild(content);
      popup.appendContent(content);
    } else if (!isMobile && popup.content.contains(content)) {
      popup.content.removeChild(content);
      container.appendChild(content);
    }
  }

  removeResizeEvent() {
    window.removeEventListener("resize", this.resizeEvent);
  }

  /**
   * 스타일을 변경 할 폴리곤 엔티티.</br>
   * Polygon entity to modify the style.
   * @type {PolygonEntity}
   * @readonly
   */
  set polygonEntity(polygonEntity) {
    this._polygonEntity = polygonEntity;
  }
  get polygonEntity() {
    return this._polygonEntity;
  }

  /*
   * 버텍스 사용 설정 변경.</br>
   * Set vertex checkbox
   * @param {boolean} value 버택스 사용 여부.</br>Vertex Usage.
   */
  setCheckBox(state) {
    this.vertexCheckBox.checked = state;
  }

  /**
   * 위젯 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the widget.  Should be called if permanently
    removing the widget from layout.
   */
  destroy() {
    if (this.fullBtn instanceof Element) {
      this.fullBtn.removeEventListener("click", this.toggleExpandPopup);
    }
    super.destroy();
    this.removeResizeEvent();
    this.polygonColorAlert.destroy();
    this.lineColorAlert.destroy();
    this.widthAlert.destroy();
    this.mobileLineColorPickerPopup.destroy();
    this.mobilePolygonColorPickerPopup.destroy();
    // this.observer.disconnect();
  }

  /*
   * 버튼 이벤트 생성.</br>
   * Create a button event.
   */
  createStyleEvent() {
    // 취소 버튼
    this.cancleBtn.addEventListener("click", () => {
      this.close();
    });

    this.typeSelectInput.addEventListener("click", () => {
      this.typeSelectButton.click();
    });

    // 타입 - 셀렉트 박스 open/close
    this.typeSelectButton.addEventListener("click", () => {
      if (this.typeOptionDiv.classList.contains("hide")) {
        this.typeOptionDiv.classList.remove("hide");
        this.typeSelectDiv.classList.add("on");
        this.mobileTypeSelectPopup.open();
      } else {
        this.typeOptionDiv.classList.add("hide");
        this.typeSelectDiv.classList.remove("on");
        this.mobileTypeSelectPopup.close();
      }
      this.setMobilePopup();
    });

    // 색상 - 색상 버튼 클릭
    this.lineColorPickerOpenBtn.addEventListener("click", () => {
      // 색상 박스 생성
      if (!this.mobileLineColorPickerPopup.isOpen) {
        this.lineColorPickBox.classList.remove("hide");
        this.mobileLineColorPickerPopup.open();
        this.originInputValue = this.lineColorInput.value;
        this.setMobilePopup();
      } else {
        this.lineColorPickBox.classList.add("hide");
        this.mobileLineColorPickerPopup.close();
        this.setMobilePopup();
      }
    });

    // 색상 - 색상 버튼 클릭
    this.polygonColorPickerOpenBtn.addEventListener("click", () => {
      // 색상 박스 생성
      if (!this.mobilePolygonColorPickerPopup.isOpen) {
        this.polygonColorPickBox.classList.remove("hide");
        this.mobilePolygonColorPickerPopup.open();
        this.originPolygonInputValue = this.polygonColorInput.value;
        this.setMobilePopup();
      } else {
        this.polygonColorPickBox.classList.add("hide");
        this.mobilePolygonColorPickerPopup.close();
        this.setMobilePopup();
      }
    });

    // 타입 - 색상 옵션 클릭
    this.colorType.addEventListener("click", () => {
      this.openColorType();
      this.setMobilePopup();
      this.colorType.querySelector(".ditap-radio-btn").classList.add("on");
      this.imgType.querySelector(".ditap-radio-btn").classList.remove("on");
      this.mobileTypeSelectPopup.close();
    });

    // 타입 - 이미지 옵션 클릭
    this.imgType.addEventListener("click", () => {
      this.openImgType();
      this.setMobilePopup();
      this.imgType.querySelector(".ditap-radio-btn").classList.add("on");
      this.colorType.querySelector(".ditap-radio-btn").classList.remove("on");
      this.mobileTypeSelectPopup.close();
    });

    // 굵기 - 굵기 크기 증가 버튼 클릭
    this.addWidthBtn.addEventListener("click", () => {
      const width = Number(this.widthInput.value);
      this.widthInput.value = width + 1;
    });

    // 굵기 - 굵기 크기 감소 버튼 클릭
    this.subtractWidthBtn.addEventListener("click", () => {
      const width = Number(this.widthInput.value);
      if (width - 1 > 0) {
        this.widthInput.value = width - 1;
      }
    });

    // 굵기 - 인풋 박스 변경시, 굵기값 변경
    this.widthInput.addEventListener("keyup", (e) => {
      const width = e.target.value;
      this.widthInput.value = width;
    });

    // 정점
    this.vertexBtn.addEventListener("click", () => {
      const check = !this.vertexCheckBox.checked;
      this.setCheckBox(check);
    });

    // 색상 - 선색상 이벤트 생성
    this.createColorPickerEvent(
      this.lineColorPicker,
      this.lineColorInput,
      this.lineColorLists,
      this.lineColorAlert,
      this.mobileLineColorInput
    );

    // 색상 - 면색상 이벤트 생성
    this.createColorPickerEvent(
      this.polygonColorPicker,
      this.polygonColorInput,
      this.polygonColorLists,
      this.polygonColorAlert,
      this.mobilePolygonColorInput
    );

    // 확인 - 엔티티 적용
    this.okBtn.addEventListener("click", () => {
      if (this.typeSelectInput.textContent === this.colorType.textContent) {
        // 엔티티 변경 타입이 색상인 경우

        // 굵기
        const width = this.widthInput.value;

        // 선색상
        const lineColor = this.lineColorInput.value;

        // 면색상
        const polygonColor = this.polygonColorInput.value;

        // 정점
        const vertex = !this.vertexCheckBox.checked;

        // 굵기 형식이 올바른 경우
        if (Number(width) > 0 && width) {
          if (!this.checkAvailableColor(lineColor)) {
            this.lineColorAlert.open();
          } else if (!this.checkAvailableColor(polygonColor)) {
            this.polygonColorAlert.open();
          } else {
            // 색상 형식이 올바른 경우{
            // 16진수 -> 10진수 rgbObject

            // rgbObject -> Color
            const lineDitapColor = Color.fromCssColorString(lineColor);
            lineDitapColor.alpha = this.lineColorPicker.color.alpha;

            const polygonDitapColor = Color.fromCssColorString(polygonColor);
            polygonDitapColor.alpha = this.polygonColorPicker.color.alpha;

            const polygonDitapMaterial = new Material({
              fabric: {
                type: "Color",
                uniforms: {
                  color: polygonDitapColor,
                },
              },
            });
            const depthFailPolygonDitapColor = new Material({
              fabric: {
                type: "Color",
                uniforms: {
                  color: Color.GRAY.withAlpha(0.3),
                },
              },
            });

            this.polygonEntity.setAllEntitiesShow(true);
            this.polygonEntity.setPolylineWidth(width);
            this.polygonEntity.setAllPointsOutlineColor(lineDitapColor);
            this.polygonEntity.setPolylineMaterial(lineDitapColor);
            this.polygonEntity.setPolylineDepthFailMaterial(lineDitapColor);
            this.polygonEntity.setPolygonDepthFailMaterial(
              depthFailPolygonDitapColor
            );
            this.polygonEntity.setPolygonMaterial(polygonDitapMaterial);
            this.polygonEntity.setAllPointsPixelSize(Number(width) * 2 + 2);

            if (!vertex) {
              this.polygonEntity.setAllPointEntitiesShow(false);
            } else {
              this.polygonEntity.setAllPointEntitiesShow(true);
            }

            this.close();
          }
        } else {
          this.widthAlert.open();
        }
      } else if (
        this.typeSelectInput.textContent === this.imgType.textContent
      ) {
        // 다이탭 기본로고 url
        const blobUrl = Utils.getUrlFromBase64(this.base64Data);

        // 엔티티 변경 타입이 이미지인 경우
        // const material = new ImageMaterialProperty({
        //   image: blobUrl,
        //   transparent: true,
        // });
        const material = new Material({
          fabric: {
            type: "Image",
            uniforms: {
              image: blobUrl,
            },
          },
        });

        this.polygonEntity.setAllPointEntitiesShow(false);
        // this.polygonEntity.setPolylineEntitiesShow(false);
        // Primitive가 Polyline의 Callback에서 제어가 되고있어
        // show=false가 적용 될 경우 Interaction에서 동작하지 않음.
        this.polygonEntity.setPolylineMaterial(Color.TRANSPARENT);
        this.polygonEntity.setPolygonMaterial(material);

        const depthFailPolygonDitapColor = new Material({
          fabric: {
            type: "Color",
            uniforms: {
              color: Color.GRAY.withAlpha(0.3),
            },
          },
        });
        this.polygonEntity.setPolygonDepthFailMaterial(
          depthFailPolygonDitapColor
        );

        // 정점
        this.polygonEntity.setAllPointEntitiesShow(false);

        this.close();
      }
    });
  }

  /**
   * 팝업 닫기.</br>
   * Close popup.
   */
  close() {
    super.close();
    this.mobileLineColorPickerPopup.close();
    this.mobileLineColorPickerPopup.destroy();
    this.mobilePolygonColorPickerPopup.close();
    this.mobilePolygonColorPickerPopup.destroy();
    this.mobileTypeSelectPopup.close();
    this.mobileTypeSelectPopup.destroy();
  }

  /*
   * 컬러픽커 이벤트 생성.</br>
   * Create a color picker event.
   * @param {Element} colorBtn 닫기 버튼 요소.</br>The Element of colorBtn.
   * @param {Element} colorPickBox 컬러픽커 박스 요소.</br>The Element of colorPickBox.
   * @param {Element} colorPicker 컬러픽커 요소.</br>The Element of colorPicker.
   * @param {Element} colorInput 색상 입력 요소.</br>The Element of colorInput.
   * @param {Element} colorLists 색상 목록 요소.</br>The Element of colorLists.
   * @param {Element} otherColorPickBox 외곽선 컬러픽커 박스 요소.</br>The Element of otherColorPickBox.
   */
  createColorPickerEvent(
    colorPicker,
    colorInput,
    colorLists,
    alert,
    mobileColorInput
  ) {
    // 색상 - 컬러픽커에서 색상변경시, 색생값 변경
    colorPicker.on("color:change", (color) => {
      const hex = Color.fromBytes(
        color.red,
        color.green,
        color.blue
      ).toCssHexString();

      // 색상 인풋박스 값 변경
      colorInput.value = hex.toUpperCase();
      mobileColorInput.value = hex.toUpperCase();
    });

    // 색상 - 인풋 박스 변경시, 색상값 변경
    colorInput.addEventListener("keyup", (e) => {
      const color = e.target.value.toUpperCase();
      this.changeColorPickerByInput(color, colorPicker, alert, colorInput);
    });

    // 색상 - 인풋 박스 변경시, 색상값 변경
    mobileColorInput?.addEventListener("keyup", (e) => {
      const color = e.target.value.toUpperCase();
      this.changeColorPickerByInput(color, mobileColorInput);
    });

    // 색상 - 리스트 클릭 시, 색상값 변경
    Array.from(colorLists).forEach((colorLi) => {
      colorLi.addEventListener("click", () => {
        const rgbColor = window.getComputedStyle(colorLi).backgroundColor;
        this.mobileTypeSelectPopup.close();

        const byteColor = Color.fromCssColorString(rgbColor).toBytes();
        colorPicker.color.red = byteColor[0];
        colorPicker.color.green = byteColor[1];
        colorPicker.color.blue = byteColor[2];
      });
    });
  }

  /*
   * 색상 변경 팝업 요소 생성.</br>
   * Create PopupStylePolygon Element
   * @returns {object} PopupStylePolygon 요소.</br>The PopupStylePolygon Element.
   */
  createStyleBox() {
    this.wrapper.classList.add(
      "ditap-mobile-bottom",
      "header-top",
      "draggable"
    );
    // # 헤더
    const title = document.createElement("h2");
    title.classList.add("ditap-popup-title");
    title.innerText = "스타일";

    // # 컨텐츠
    const subTitle = document.createElement("p");
    subTitle.classList.add("ditap-popup-subtitle");
    subTitle.innerText = "아래의 스타일을 선택해주세요.";
    const styleList = document.createElement("div");
    styleList.classList.add("ditap-popup-style-list");
    styleList.innerHTML = `
      <div class="ditap-style-item ditap-line">
        <p class="ditap-style-title">스타일</p>
        <div class="ditap-style-inp-wrap ditap-style-select ditap-style-select js-ditap-style-select">
          <div class="ditap-style-inp js-ditap-style-type">색상</div>
          <button class="ditap-dropbox-img ditap-custom-button js-ditap-style-type-button"></button>
          <div class="ditap-style-opt js-ditap-style-type-opt hide">
            <ul class="ditap-style-opt-list">
              <button class="ditap-style-opt-title js-ditap-style-type-opt-color-btn ditap-custom-button">색상
              <div class="ditap-radio-btn on"></div>
              </button>
              <button class="ditap-style-opt-title js-ditap-style-type-opt-img-btn ditap-custom-button">이미지
              <div class="ditap-radio-btn"></div>
              </button>
            </ul>
          </div>
        </div>
      </div>
      
      <div class="ditap-style-item ditap-style-clipping-item js-ditap-vertex-div">
        <p class="ditap-style-title">정점</p>
        <p class="ditap-clipping-item js-ditap-vertex-item" style=""  >
          <input class="ditap-chk js-ditap-vertex-check-box" type="checkbox" value="X">
          <label class="ditap-label">
            <button class="ditap-label-title ditap-custom-button">도형의 정점을 강조하여 표시합니다.</button>
          </label>
        </p>
      </div>

      <div class="ditap-style-item ditap-line js-ditap-style-width-box">
        <p class="ditap-style-title js-ditap-style-width-box">굵기</p>
        <div class="ditap-style-inp-wrap">
          <label for="width1">
            <input id="width1" type="number" class="ditap-style-inp js-ditap-style-width" value="5" />
          </label>
          <button class="ditap-add-btn js-ditap-style-add-width ditap-custom-button"></button>
          <button class="ditap-subtract-btn js-ditap-style-subtract-width ditap-custom-button"></button>
        </div>
      </div>
      <div class="ditap-style-color-wrap js-ditap-style-line-color-box">
        <div class="ditap-style-item">
          <p class="ditap-style-title">선색상</p>
          <div class="ditap-style-inp-wrap js-ditap-style-color-btn">
            <input type="text" class="ditap-style-inp js-ditap-style-color" maxlength="7" value="#FFFFFF"></input>
            <button class="ditap-palette-img js-ditap-color-open-btn ditap-custom-button"></button>
          </div>
        </div>
        <div class="ditap-style-color  js-ditap-style-color-pickBox hide">
          <div class="js-ditap-style-color-picker"> </div>
          <div style="height: 100px;">
              <ul class="ditap-style-color-list js-ditap-style-color-list">
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #6127FF;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #0067F4;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #00D0FF;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #1BC47D;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #6FCF97;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #FF2C60;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #FF5A83;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #000000;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #1C1C1C;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #333333;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #828282;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #BDBDBD;"></button>
              </ul>
          </div>
        </div>
      </div>
      <div class="ditap-style-color-wrap js-ditap-style-polygon-color-box">
        <div class="ditap-style-item">
          <p class="ditap-style-title">면색상</p>
          <div class="ditap-style-inp-wrap js-ditap-style-color-btn">
            <input type="text" class="ditap-style-inp js-ditap-style-color" maxlength="7" value="#FFFFFF"></input>
            <button class="ditap-palette-img js-ditap-color-open-btn ditap-custom-button"></button>
          </div>
        </div>
        <div class="ditap-style-color  js-ditap-style-color-pickBox hide">
          <div class="js-ditap-style-color-picker"> </div>
          <div style="height: 100px;">
            <ul class="ditap-style-color-list js-ditap-style-color-list">
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #6127FF;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #0067F4;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #00D0FF;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #1BC47D;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #6FCF97;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #FF2C60;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #FF5A83;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #000000;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #1C1C1C;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #333333;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #828282;"></button>
              <button class="ditap-style-color-item ditap-custom-button" style="background-color: #BDBDBD;"></button>
            </ul>
          </div>
        </div>
      </div>

      <div class="ditap-style-item ditap-style-texture-wrap  js-ditap-style-texture-box hide">
        <p class="ditap-style-title">텍스처</p>
        <div class="ditap-style-texture js-ditap-style-texture-img"></div>
      </div>
      
      `;

    const btnWrap = document.createElement("div");
    btnWrap.classList.add("ditap-btn-wrap");
    btnWrap.innerHTML = `<button class="ditap-popup-btn js-ditap-style-cancle-btn">취소</button><button class="ditap-popup-btn js-ditap-style-ok-btn gold">확인</button>`;

    return {
      header: {
        title: title,
      },
      content: {
        subTitle: subTitle,
        styleList: styleList,
      },
      footer: {
        btnWrap: btnWrap,
      },
    };
  }

  changeColorPickerByInput(color, colorPicker, alert, colorInput) {
    if (color.length === 7) {
      if (this.checkAvailableColor(color)) {
        // 16진수 -> 10진수
        const byteColor = Color.fromCssColorString(color).toBytes();
        colorPicker.color.red = byteColor[0];
        colorPicker.color.green = byteColor[1];
        colorPicker.color.blue = byteColor[2];
      } else {
        alert.open();
        colorInput.value = "#FFFFFF";
        colorPicker.color.red = 255;
        colorPicker.color.green = 255;
        colorPicker.color.blue = 255;
      }
    }
  }
  /*
   * 컬러픽커 생성.</br>
   * Create ColorPicker to change the style of a LinestringEntity.
   * @param {Element} pickerDiv 부모 요소.</br>The Element of pickerDiv.
   * @returns {iro.ColorPicker} 생성된 컬러픽커.</br>The ColorPicker.
   */
  createColorPicker(pickerDiv) {
    // 색상 - 컬러픽커 생성
    const width = Utils.getColorPickerWidth();
    const colorPicker = new iro.ColorPicker(pickerDiv, {
      width: width,
      boxHeight: 180,
      color: "rgb(0, 0, 0)",
      // borderWidth: 1,
      // borderColor: "#fff",
      layout: [
        {
          component: iro.ui.Box,
        },
        {
          component: iro.ui.Slider,
          options: {
            sliderType: "hue",
            sliderSize: 10,
          },
        },
        {
          component: iro.ui.Slider,
          options: {
            sliderType: "alpha",
            sliderSize: 10,
          },
        },
      ],
    });

    return colorPicker;
  }

  /*
   * 색상 유형 모드의 팝업 열기.</br>
   * Opens a popup of color type mode.
   */
  openColorType() {
    // 굵기, 색상 div 활성화, icon div 비활성화
    this.widthBox.classList.remove("hide");
    this.lineColorBox.classList.remove("hide");
    this.polygonColorBox.classList.remove("hide");
    this.vertexDiv.classList.remove("hide");

    this.textureBox.classList.add("hide");
    this.typeOptionDiv.classList.add("hide");
    this.typeSelectDiv.classList.remove("on");
    this.typeSelectInput.textContent = this.colorType.textContent;
  }

  /*
   * 이미지 유형 모드의 팝업 열기.</br>
   * Opens a popup of image type mode.
   */
  openImgType() {
    // 굵기, 색상 Box 활성화, icon Box 비활성화
    this.widthBox.classList.add("hide");
    this.lineColorBox.classList.add("hide");
    this.polygonColorBox.classList.add("hide");
    this.vertexDiv.classList.add("hide");

    this.textureBox.classList.remove("hide");
    this.typeOptionDiv.classList.add("hide");
    this.typeSelectDiv.classList.remove("on");
    this.typeSelectInput.textContent = this.imgType.textContent;
  }

  /*
   * 기존 엔티티 스타일 적용.</br>
   * Apply the style of the existing Entity.
   * @param {PolygonEntity} polygonEntity 스타일 적용할 PolygonEntity 인스턴스.</br> The instance of PolygonEntity to apply
   */
  setPopupStylePolygon(polygonEntity) {
    if (polygonEntity.polygonPrimitive) {
      // 폴리곤의 material이 image인지 확인
      const material = polygonEntity.polygonPrimitive.appearance.material;

      if (material.type === "Image") {
        // image인 경우
        this.openImgType();
      } else if (material.type === "Color") {
        //color인 경우
        this.openColorType();

        // INPUT 굵기 적용
        this.widthInput.value = polygonEntity.polylineEntity.polyline.width;

        // INPUT 선색상 적용
        const lineColor = polygonEntity.polylineEntity.polyline.material.color.getValue();
        this.lineColorPicker.color.red = lineColor.red * 255;
        this.lineColorPicker.color.green = lineColor.green * 255;
        this.lineColorPicker.color.blue = lineColor.blue * 255;
        this.lineColorPicker.color.alpha = lineColor.alpha;

        // INPUT 면색상 적용;
        const polygonColor = material.uniforms.color;
        this.polygonColorPicker.color.red = polygonColor.red * 255;
        this.polygonColorPicker.color.green = polygonColor.green * 255;
        this.polygonColorPicker.color.blue = polygonColor.blue * 255;
        this.polygonColorPicker.color.alpha = polygonColor.alpha;
      }
    }

    // 정점 적용
    const pointEntities = polygonEntity.pointEntities;

    // 정점 체크박스 체크
    if (pointEntities.length > 0 && pointEntities[0].point.show.getValue()) {
      this.setCheckBox(false);
    } else {
      this.setCheckBox(true);
    }
  }

  /*
   * HEX 색상 코드를 사용 여부 조회.</br>
   * Check if it is a usable HEX color code.
   * @param {string} color 확인할 색상 코드.</br>Color code to check(ex: "#000000").
   * @returns {boolean} 사용 가능 여부.</br>Usable Status.
   */
  checkAvailableColor(color) {
    if (Color.fromCssColorString(color)) {
      return true;
    }

    return false;
  }
}
export default PopupStylePolygon;
